import { STATE_CODE_TO_REGION_ID, US_REGIONS, US_REGIONS_BY_ID } from '../constants/regions';

const toUpper = (value) => (value == null ? '' : String(value).trim().toUpperCase());

/** Normalize a single value or array into an array. */
export const toItemArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value == null) return [];
  return [value];
};

/**
 * Extract a state/territory code from a filter selection item.
 * State autocomplete items use `{ id: 'CA', value: 'California' }`.
 */
export const extractStateCode = (entry) => {
  if (entry == null || entry === '') return null;
  if (typeof entry === 'string' || typeof entry === 'number') {
    const code = toUpper(entry);
    return code || null;
  }
  if (typeof entry === 'object') {
    const code = entry.id || entry.code || entry.abbreviation;
    if (code != null && String(code).trim() !== '') {
      return toUpper(code);
    }
  }
  return null;
};

export const extractStateCodes = (selectedStates = []) => {
  return [...new Set(toItemArray(selectedStates).map(extractStateCode).filter(Boolean))];
};

/** Normalize region selection to an array of region id strings. */
export const normalizeRegionIds = (regions = []) => {
  const ids = toItemArray(regions)
    .map((entry) => {
      if (entry == null || entry === '') return null;
      if (typeof entry === 'string') return entry;
      if (typeof entry === 'object') return entry.id || null;
      return null;
    })
    .filter(Boolean);

  return [...new Set(ids)];
};

export const toStateFilterItem = (state) => ({
  id: state.code,
  value: state.name,
  multipleId: [state.code, state.name],
});

export const getStatesForRegions = (regionIds = []) => {
  const ids = normalizeRegionIds(regionIds);
  const byCode = new Map();

  ids.forEach((regionId) => {
    const region = US_REGIONS_BY_ID[regionId];
    if (!region) return;
    region.states.forEach((state) => {
      byCode.set(state.code.toUpperCase(), state);
    });
  });

  return [...byCode.values()];
};

/**
 * @returns {string[]|null} Allow-list of state codes, or `null` when no region limit applies.
 */
export const getAllowedStateCodes = (regionIds = []) => {
  const ids = normalizeRegionIds(regionIds);
  if (ids.length === 0) return null;
  return getStatesForRegions(ids).map((state) => state.code.toUpperCase());
};

export const getOptionStateCode = (option) => {
  if (option == null) return '';
  if (typeof option === 'string' || typeof option === 'number') {
    return String(option).trim().toUpperCase();
  }
  return String(option.id || option.code || option.abbreviation || '')
    .trim()
    .toUpperCase();
};

/** Stable key for memoizing allowed-code lists (locale-aware sort). */
export const toAllowedCodesKey = (allowedStateCodes) => {
  if (!Array.isArray(allowedStateCodes) || allowedStateCodes.length === 0) {
    return '';
  }
  return allowedStateCodes
    .map((code) => String(code).toUpperCase())
    .sort((a, b) => a.localeCompare(b))
    .join('|');
};

/** Client-side filter of autocomplete options by allowed state codes. */
export const filterOptionsByAllowedStateCodes = (options, allowedStateCodes) => {
  const key = toAllowedCodesKey(allowedStateCodes);
  if (!key) return options || [];

  const allow = new Set(key.split('|').filter(Boolean));
  return (options || []).filter((option) => allow.has(getOptionStateCode(option)));
};

const dedupeStateItems = (items = []) => {
  const byCode = new Map();
  items.forEach((item) => {
    const code = extractStateCode(item);
    if (!code) return;
    if (!byCode.has(code)) {
      byCode.set(code, item);
    }
  });
  return [...byCode.values()];
};

/**
 * Reconcile selected states when the region multi-select changes.
 *
 * - Empty next regions: keep currently selected states (no limit).
 * - Non-empty next regions: keep selected states that remain allowed, add all
 *   states from newly added regions, drop states outside remaining regions.
 */
export const reconcileStatesOnRegionChange = ({ prevRegions = [], nextRegions = [], selectedStates = [] } = {}) => {
  const prevIds = normalizeRegionIds(prevRegions);
  const nextIds = normalizeRegionIds(nextRegions);
  const currentStates = Array.isArray(selectedStates) ? selectedStates : [];

  if (nextIds.length === 0) {
    return dedupeStateItems(currentStates);
  }

  const allowedCodes = new Set(getAllowedStateCodes(nextIds) || []);
  const prevSet = new Set(prevIds);
  const addedRegionIds = nextIds.filter((id) => !prevSet.has(id));

  const retained = currentStates.filter((item) => {
    const code = extractStateCode(item);
    return code && allowedCodes.has(code);
  });

  const addedStates = getStatesForRegions(addedRegionIds).map(toStateFilterItem);

  return dedupeStateItems([...retained, ...addedStates]);
};

/**
 * Best-effort reopen hydration: include a region only when every one of its
 * states is present in the selected set (unambiguous complete membership).
 */
export const deriveRegionsFromStates = (selectedStates = []) => {
  const selectedCodes = new Set(extractStateCodes(selectedStates));
  if (selectedCodes.size === 0) return [];

  return US_REGIONS.filter((region) => region.states.every((state) => selectedCodes.has(state.code.toUpperCase()))).map(
    (region) => region.id
  );
};

/** Formik-only region initial values for Location modals with hasRegionLimiter. */
export const getRegionFormikInitialValues = ({ enabled, stateData } = {}) => {
  if (!enabled) return {};
  return {
    region_data: deriveRegionsFromStates(Array.isArray(stateData) ? stateData : []),
  };
};

/**
 * Apply a region multi-select change onto Location Formik values
 * (state population, show/type, optional county prune).
 */
export const applyRegionChangeToLocationValues = ({
  values = {},
  nextRegions = [],
  hasCountyFilter = false,
  defaultStateType,
} = {}) => {
  const prevRegions = Array.isArray(values.region_data) ? values.region_data : [];
  const selectedStates = Array.isArray(values.state_data) ? values.state_data : [];
  const nextStates = reconcileStatesOnRegionChange({
    prevRegions,
    nextRegions,
    selectedStates,
  });

  const nextValues = {
    region_data: nextRegions,
    state_data: nextStates,
  };

  if (nextRegions.length > 0 || nextStates.length > 0) {
    nextValues.state_show = true;
    if (!values.state_type && defaultStateType) {
      nextValues.state_type = defaultStateType;
    }
  }

  if (hasCountyFilter) {
    const remainingCodes = extractStateCodes(nextStates);
    nextValues.county_data = pruneCountiesForStates({
      counties: Array.isArray(values.county_data) ? values.county_data : [],
      allowedStateCodes: remainingCodes.length > 0 ? remainingCodes : null,
    });
  }

  return nextValues;
};

/**
 * Extract a state code from a county filter item (value like "Travis, TX").
 */
export const extractCountyStateCode = (county) => {
  if (county == null) return null;
  if (typeof county !== 'object') return null;

  if (county.state) return toUpper(county.state);

  if (Array.isArray(county.multipleId)) {
    const fromMultiple = [...county.multipleId].reverse().find((part) => {
      const code = toUpper(part);
      return code.length === 2 || STATE_CODE_TO_REGION_ID[code];
    });
    if (fromMultiple) return toUpper(fromMultiple);
  }

  if (typeof county.value === 'string' && county.value.includes(',')) {
    const parts = county.value.split(',');
    return toUpper(parts[parts.length - 1]);
  }

  return null;
};

/**
 * Drop counties whose state is no longer in the allowed/selected state set.
 * When `allowedStateCodes` is null/empty, returns counties unchanged.
 */
export const pruneCountiesForStates = ({ counties = [], allowedStateCodes = null } = {}) => {
  if (!Array.isArray(counties)) return [];
  if (counties.length === 0) return counties;
  if (!allowedStateCodes?.length) return counties;

  const allowed = new Set(allowedStateCodes.map(toUpper));
  return counties.filter((county) => {
    const stateCode = extractCountyStateCode(county);
    return Boolean(stateCode && allowed.has(stateCode));
  });
};

/**
 * When opening a chip for a hidden child of a region-limited Location multiple,
 * return the parent filter option so the Region selector is visible.
 */
export const resolveOpenFilterOption = (filter, filterOptions = []) => {
  if (!filter || filter.multiple) return filter;

  const parent = (filterOptions || []).find(
    (option) =>
      option?.options?.hasRegionLimiter &&
      Array.isArray(option?.multiple?.filters) &&
      option.multiple.filters.includes(filter.value)
  );

  return parent || filter;
};

/** Hydrate Formik-prefixed state for a multiple filter from Redux fields. */
export const buildMultipleFilterState = (filter, fields = {}) => {
  const childFilters = filter?.multiple?.filters;
  if (!Array.isArray(childFilters)) return {};

  const nextState = {};
  childFilters.forEach((filterValue) => {
    const fieldState = fields[filterValue] ?? {};
    Object.keys(fieldState).forEach((propKey) => {
      nextState[`${filterValue}_${propKey}`] = fieldState[propKey];
    });
  });
  return nextState;
};
